import React, { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Receipt, UploadCloud, FileText, Eye, Trash2, Sparkles, Loader2,
  AlertTriangle, CheckCircle2, Building2, CalendarDays, Hash, Globe2, Landmark, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, apiErrorMessage } from "@/lib/api";

const ESTADO_BADGE = {
  extraida: { label: "Leída con IA", cls: "bg-emerald-600 hover:bg-emerald-600", icon: CheckCircle2 },
  proforma: { label: "Proforma (no se contabiliza)", cls: "bg-amber-500 hover:bg-amber-500", icon: AlertTriangle },
  error: { label: "Error de lectura", cls: "bg-destructive hover:bg-destructive", icon: AlertTriangle },
};

const DataRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    {Icon && <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="truncate text-sm font-medium">{value || "—"}</p>
    </div>
  </div>
);

const FacturaCard = ({ factura, reservaType, onView, onReplace, onDelete, busy }) => {
  const est = factura.estado;
  const badge = ESTADO_BADGE[est];
  const ext = factura.extraccion || {};
  const prov = ext.proveedor || {};
  const cuenta = factura.cuenta_gasto || {};
  const rows = factura.holded_rows || [];
  const provider = factura.proveedor_match;

  return (
    <div className="rounded-xl border bg-card p-4" data-testid={`factura-card-${factura.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {badge && <Badge className={`text-white ${badge.cls}`} data-testid="factura-estado"><badge.icon className="mr-1 h-3 w-3" />{badge.label}</Badge>}
          <span className="flex items-center gap-1 text-xs text-muted-foreground"><FileText className="h-3.5 w-3.5" />{factura.filename}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={() => onView(factura)} data-testid={`factura-view-${factura.id}`}><Eye className="mr-1.5 h-4 w-4" />Ver PDF</Button>
          <Button variant="outline" size="sm" onClick={() => onReplace(factura)} disabled={busy} data-testid={`factura-replace-${factura.id}`}><UploadCloud className="mr-1.5 h-4 w-4" />Reemplazar</Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(factura)} disabled={busy} data-testid={`factura-delete-${factura.id}`} aria-label="Eliminar factura"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      {est === "error" && (
        <p className="mt-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          No se pudieron extraer los datos: {factura.error || "error desconocido"}. Puedes reemplazar el archivo e intentarlo de nuevo.
        </p>
      )}
      {est === "proforma" && (
        <p className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          El documento es una <strong>proforma</strong>: no genera registro contable. Cuando recibas la factura definitiva, súbela con "Reemplazar".
        </p>
      )}

      {est === "extraida" && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3 rounded-lg border bg-muted/20 p-3 sm:grid-cols-4">
            <DataRow icon={Hash} label="Nº factura" value={ext.numero_factura} />
            <DataRow icon={CalendarDays} label="Fecha" value={ext.fecha} />
            <DataRow icon={Building2} label="Proveedor (factura)" value={prov.nombre} />
            <DataRow icon={Hash} label="NIF" value={prov.nif} />
            <DataRow icon={Globe2} label="País" value={prov.pais} />
            <DataRow label="Moneda" value={`${ext.moneda || "EUR"}${ext.tipo_cambio && ext.tipo_cambio !== 1 ? ` · ${ext.tipo_cambio}` : ""}`} />
            <DataRow icon={CalendarDays} label="Vencimiento" value={ext.fecha_vencimiento} />
            <DataRow label="Descripción" value={ext.descripcion} />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-lg border p-3">
              <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><Landmark className="h-3.5 w-3.5" />Cuenta de gasto (Holded)</p>
              {cuenta.numero
                ? <p className="text-sm"><span className="font-mono font-semibold">{cuenta.numero}</span> · {cuenta.nombre}</p>
                : <p className="text-sm text-amber-700">Sin cuenta automática para «{reservaType}» en este país. La asignará Contabilidad.</p>}
            </div>
            <div className="rounded-lg border p-3">
              <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><Building2 className="h-3.5 w-3.5" />Proveedor en maestro SOFI</p>
              {provider
                ? <p className="text-sm">{provider.nombre}{provider.cuenta_contable ? <> · acreedor <span className="font-mono">{provider.cuenta_contable}</span></> : ""}{provider.holded_clave_operacion ? <> · op. {provider.holded_clave_operacion}</> : ""}{provider.holded_codigo_pais ? <> · {provider.holded_codigo_pais}</> : ""}</p>
                : <p className="text-sm text-amber-700">No localizado en el maestro de proveedores.</p>}
            </div>
          </div>
          {rows.length > 0 && (
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Concepto</TableHead>
                  <TableHead className="text-right">Base</TableHead>
                  <TableHead className="w-16 text-center">IVA %</TableHead>
                  <TableHead className="w-16 text-center">Ret. %</TableHead>
                  <TableHead className="w-14 text-center">ISP</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="max-w-[280px] truncate">{r["Concepto"]}</TableCell>
                      <TableCell className="text-right tabular-nums">{r["Precio unidad"]}</TableCell>
                      <TableCell className="text-center tabular-nums">{r["IVA %"]}</TableCell>
                      <TableCell className="text-center tabular-nums">{r["Retención %"]}</TableCell>
                      <TableCell className="text-center tabular-nums">{r["Inv. Suj. Pasivo (1/0)"]}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const FacturaIA = ({ tripId, reservaId, reservaType, onUploaded }) => {
  const fileRef = useRef(null);
  const replaceId = useRef(null); // id de la factura a reemplazar; null = añadir nueva
  const [busy, setBusy] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["factura-reserva", tripId, reservaId],
    queryFn: async () => (await api.get(`/trips/${tripId}/reservas/${reservaId}/factura`)).data,
  });

  const facturas = data?.facturas || [];
  const provider = data?.provider;
  const isMonthly = data?.is_monthly;

  const pickAdd = () => { replaceId.current = null; fileRef.current?.click(); };
  const pickReplace = (f) => { replaceId.current = f.id; fileRef.current?.click(); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.type && f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Sube un archivo PDF de la factura");
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    const rid = replaceId.current;
    setBusy(true);
    try {
      const url = rid
        ? `/trips/${tripId}/reservas/${reservaId}/factura/${rid}`
        : `/trips/${tripId}/reservas/${reservaId}/factura`;
      const method = rid ? "put" : "post";
      const res = await api[method](url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const est = res.data?.factura?.estado;
      if (est === "extraida") toast.success(rid ? "Factura reemplazada y leída con IA" : "Factura leída con IA y registrada");
      else if (est === "proforma") toast.info("Es una proforma: no se contabiliza");
      else toast.warning("Factura subida, pero la IA no pudo leerla");
      refetch();
      onUploaded && onUploaded(res.data?.factura, res.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "No se pudo procesar la factura"));
    } finally {
      setBusy(false);
      replaceId.current = null;
    }
  };

  const onView = async (f) => {
    try {
      const res = await api.get(`/trips/${tripId}/reservas/${reservaId}/factura/${f.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const onDelete = async (f) => {
    setBusy(true);
    try {
      await api.delete(`/trips/${tripId}/reservas/${reservaId}/factura/${f.id}`);
      toast.success("Factura eliminada");
      refetch();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return <Skeleton className="h-32 w-full" data-testid="factura-ia-loading" />;

  if (isMonthly) {
    return (
      <Card data-testid="factura-ia-monthly">
        <CardContent className="flex items-start gap-3 p-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700"><Building2 className="h-5 w-5" /></div>
          <div>
            <p className="font-semibold">Proveedor de facturación mensual</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {provider?.nombre ? <strong>{provider.nombre}</strong> : "Este proveedor"} emite <strong>una factura mensual</strong> con varios servicios.
              Su factura no se sube aquí: se gestiona en el módulo <strong>Finanzas → Facturas → Mensuales</strong>.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="factura-ia">
      <CardContent className="space-y-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Facturas del proveedor (lectura con IA)</h3>
            {facturas.length > 0 && <Badge variant="secondary" data-testid="factura-count">{facturas.length}</Badge>}
          </div>
          {facturas.length > 0 && (
            <Button size="sm" onClick={pickAdd} disabled={busy} data-testid="factura-add">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}Añadir factura
            </Button>
          )}
        </div>

        <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFile} data-testid="factura-file-input" />

        {facturas.length === 0 ? (
          <button
            type="button"
            onClick={pickAdd}
            disabled={busy}
            className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-muted-foreground/30 bg-muted/30 px-6 py-10 text-center transition-colors hover:border-primary/50 hover:bg-muted/50 disabled:opacity-60"
            data-testid="factura-dropzone"
          >
            {busy ? <Loader2 className="h-8 w-8 animate-spin text-primary" /> : <UploadCloud className="h-8 w-8 text-muted-foreground" />}
            <span className="text-sm font-medium">{busy ? "Leyendo la factura con IA…" : "Sube el PDF de la factura del proveedor"}</span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground"><Sparkles className="h-3 w-3" /> Puedes subir varias facturas. La IA extraerá los campos para Holded</span>
          </button>
        ) : (
          <div className="space-y-3">
            {facturas.map((f) => (
              <FacturaCard key={f.id} factura={f} reservaType={reservaType} onView={onView} onReplace={pickReplace} onDelete={onDelete} busy={busy} />
            ))}
            <p className="text-xs text-muted-foreground">
              Datos en solo lectura. El Excel semanal para Holded se genera en el módulo de <strong>Contabilidad</strong>, donde el contable puede revisarlos y ajustarlos.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
